/**
 * OnboardingFlow - Welcome tutorial for new users
 * Shows on first launch to introduce key features
 */

import React, { useState, useEffect } from 'react';

const ONBOARDING_STEPS = [
  {
    id: 'welcome',
    title: 'Welcome to FloorPlan Pro',
    subtitle: 'Your complete floor plan and HVAC design tool',
    description: 'Create professional floor plans, calculate HVAC loads, and generate detailed reports - all from your phone or desktop.',
    icon: '🏠',
    tips: [
      'Draw walls, rooms, doors & windows',
      'Add furniture and annotations',
      'Calculate HVAC loads (Manual J)',
      'Export to PDF or image',
    ],
  },
  {
    id: 'drawing',
    title: 'Drawing Tools',
    subtitle: 'Create your floor plan',
    description: 'Use the toolbar to select drawing tools. Tap or click to place points, and the app will create walls, rooms, and more.',
    icon: '✏️',
    tips: [
      'Wall Tool - Draw walls by tapping start and end points',
      'Room Tool - Create enclosed rooms with labels',
      'Door/Window - Add to existing walls',
      'Pinch to zoom, drag to pan the canvas',
    ],
  },
  {
    id: 'rooms',
    title: 'Rooms & Labels',
    subtitle: 'Organize your floor plan',
    description: 'Add rooms to define spaces. Each room can have a name, type, and the app will calculate its area automatically.',
    icon: '🏢',
    tips: [
      'Tap Room tool, then tap corners to draw',
      'Double-tap or click "Done" to complete',
      'Select a room to edit its properties',
      'Room areas are calculated automatically',
    ],
  },
  {
    id: 'hvac',
    title: 'HVAC Mode',
    subtitle: 'Design your duct system',
    description: 'Switch to HVAC mode to draw ductwork, place vents, and calculate heating/cooling loads based on Manual J methodology.',
    icon: '❄️',
    tips: [
      'Tap the HVAC button to enter HVAC mode',
      'Draw supply (blue) and return (orange) ducts',
      'Place registers and return grilles',
      'Use Load Calc for sizing recommendations',
    ],
  },
  {
    id: 'loadcalc',
    title: 'Load Calculations',
    subtitle: 'Size your HVAC system',
    description: 'Get heating and cooling load estimates based on your floor plan or enter building details manually.',
    icon: '📊',
    tips: [
      'From Floor Plan - uses drawn rooms',
      'Manual Entry - enter sq ft, insulation, etc.',
      'Adjust climate zone and conditions',
      'See recommended equipment sizes',
    ],
  },
  {
    id: 'export',
    title: 'Export & Share',
    subtitle: 'Save and share your work',
    description: 'Export your floor plan as a PDF or image. Save projects locally and load them later.',
    icon: '📤',
    tips: [
      'Export to PDF with scale and title block',
      'Save as PNG or JPG image',
      'Save/Load projects from File menu',
      'Projects are saved in your browser',
    ],
  },
];

export default function OnboardingFlow({ onComplete, onSkip }) {
  const [currentStep, setCurrentStep] = useState(0);
  const [isVisible, setIsVisible] = useState(true);

  const step = ONBOARDING_STEPS[currentStep];
  const isFirstStep = currentStep === 0;
  const isLastStep = currentStep === ONBOARDING_STEPS.length - 1;

  const handleNext = () => {
    if (isLastStep) {
      handleComplete();
    } else {
      setCurrentStep(currentStep + 1);
    }
  };

  const handlePrev = () => {
    if (!isFirstStep) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleComplete = () => {
    localStorage.setItem('floorplan-onboarding-complete', 'true');
    setIsVisible(false);
    onComplete?.();
  };

  const handleSkip = () => {
    localStorage.setItem('floorplan-onboarding-complete', 'true');
    setIsVisible(false);
    onSkip?.();
  };

  if (!isVisible) return null;

  return (
    <div style={styles.overlay}>
      <div style={styles.modal}>
        {/* Progress dots */}
        <div style={styles.progress}>
          {ONBOARDING_STEPS.map((_, index) => (
            <button
              key={index}
              onClick={() => setCurrentStep(index)}
              style={{
                ...styles.dot,
                ...(index === currentStep ? styles.dotActive : {}),
              }}
            />
          ))}
        </div>

        {/* Skip button */}
        <button onClick={handleSkip} style={styles.skipBtn}>
          Skip
        </button>

        {/* Content */}
        <div style={styles.content}>
          {/* Icon */}
          <div style={styles.iconWrapper}>
            <span style={styles.icon}>{step.icon}</span>
          </div>

          {/* Title */}
          <h2 style={styles.title}>{step.title}</h2>
          <p style={styles.subtitle}>{step.subtitle}</p>

          {/* Description */}
          <p style={styles.description}>{step.description}</p>

          {/* Tips */}
          <div style={styles.tipsContainer}>
            {step.tips.map((tip, index) => (
              <div key={index} style={styles.tipRow}>
                <span style={styles.tipBullet}>•</span>
                <span style={styles.tipText}>{tip}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation */}
        <div style={styles.navigation}>
          <button
            onClick={handlePrev}
            style={{
              ...styles.navBtn,
              ...(isFirstStep ? styles.navBtnDisabled : {}),
            }}
            disabled={isFirstStep}
          >
            Back
          </button>

          <span style={styles.stepCounter}>
            {currentStep + 1} / {ONBOARDING_STEPS.length}
          </span>

          <button
            onClick={handleNext}
            style={{
              ...styles.navBtn,
              ...styles.navBtnPrimary,
            }}
          >
            {isLastStep ? 'Get Started' : 'Next'}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * Check if onboarding should be shown
 */
export function shouldShowOnboarding() {
  return localStorage.getItem('floorplan-onboarding-complete') !== 'true';
}

/**
 * Reset onboarding (for testing or help menu)
 */
export function resetOnboarding() {
  localStorage.removeItem('floorplan-onboarding-complete');
}

const styles = {
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 20000,
    padding: '20px',
  },
  modal: {
    backgroundColor: '#1a2332',
    borderRadius: '16px',
    width: '100%',
    maxWidth: '420px',
    maxHeight: '90vh',
    overflow: 'auto',
    border: '1px solid rgba(0,200,255,0.2)',
    position: 'relative',
    boxShadow: '0 20px 60px rgba(0,0,0,0.5)',
  },
  progress: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    padding: '20px 20px 0',
  },
  dot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
    backgroundColor: 'rgba(255,255,255,0.2)',
    border: 'none',
    cursor: 'pointer',
    transition: 'all 0.2s ease',
    padding: 0,
  },
  dotActive: {
    backgroundColor: '#00c8ff',
    width: '24px',
    borderRadius: '4px',
  },
  skipBtn: {
    position: 'absolute',
    top: '16px',
    right: '16px',
    background: 'none',
    border: 'none',
    color: '#6080a0',
    fontSize: '13px',
    cursor: 'pointer',
    padding: '4px 8px',
  },
  content: {
    padding: '24px 28px',
    textAlign: 'center',
  },
  iconWrapper: {
    width: '80px',
    height: '80px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, rgba(0,200,255,0.2) 0%, rgba(76,175,80,0.2) 100%)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
    border: '2px solid rgba(0,200,255,0.3)',
  },
  icon: {
    fontSize: '36px',
  },
  title: {
    color: '#ffffff',
    fontSize: '22px',
    fontWeight: '700',
    margin: '0 0 6px',
  },
  subtitle: {
    color: '#00c8ff',
    fontSize: '14px',
    fontWeight: '500',
    margin: '0 0 16px',
  },
  description: {
    color: '#a0b0c0',
    fontSize: '14px',
    lineHeight: '1.5',
    margin: '0 0 20px',
  },
  tipsContainer: {
    backgroundColor: 'rgba(0,0,0,0.2)',
    borderRadius: '10px',
    padding: '16px',
    textAlign: 'left',
  },
  tipRow: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '10px',
    marginBottom: '10px',
  },
  tipBullet: {
    color: '#00c8ff',
    fontSize: '14px',
    fontWeight: 'bold',
  },
  tipText: {
    color: '#c0d0e0',
    fontSize: '13px',
    lineHeight: '1.4',
  },
  navigation: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '16px 24px 24px',
    borderTop: '1px solid rgba(255,255,255,0.1)',
  },
  navBtn: {
    padding: '10px 20px',
    borderRadius: '8px',
    border: '1px solid rgba(255,255,255,0.2)',
    backgroundColor: 'transparent',
    color: '#a0b0c0',
    fontSize: '14px',
    fontWeight: '500',
    cursor: 'pointer',
    transition: 'all 0.15s ease',
    minWidth: '90px',
  },
  navBtnDisabled: {
    opacity: 0.3,
    cursor: 'not-allowed',
  },
  navBtnPrimary: {
    backgroundColor: '#00c8ff',
    borderColor: '#00c8ff',
    color: '#000',
    fontWeight: '600',
  },
  stepCounter: {
    color: '#6080a0',
    fontSize: '12px',
  },
};
